import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { generateQuotePdf } from './quotePdf';
import SearchSelect from '../../components/SearchSelect';
import { canWrite } from '../../utils/roles';
import { parseTime } from '../../utils/format';
import { clientDisplayName } from '../../utils/clientName';
import './QuotePage.css';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_OPTS = [
  { value: 'posible',    label: 'Posible',    color: '#f59e0b' },
  { value: 'confirmado', label: 'Confirmado', color: '#22c55e' },
  { value: 'cancelado',  label: 'Cancelado',  color: '#ef4444' },
];

// ── Math ───────────────────────────────────────────────────────────────────────

function lineTotal(it) {
  return Number(it.quantity) * Number(it.unit_price) * (1 - (Number(it.discount) || 0) / 100);
}

const IVA_PCT = 22; // IVA fijo — no es editable en la cotización

function computeTotals(items, discountType, discountValue, taxRate) {
  const subtotal = items.filter(i => !i.is_header).reduce((s, i) => s + lineTotal(i), 0);
  const discAmt = discountType === 'PERCENT'
    ? subtotal * (Number(discountValue) || 0) / 100
    : Number(discountValue) || 0;
  const afterDisc = Math.max(0, subtotal - discAmt);
  const taxAmt = afterDisc * (Number(taxRate) || 0) / 100;
  return { subtotal, discAmt, afterDisc, taxAmt, total: afterDisc + taxAmt };
}

// Margen interno (no va al PDF): venta neta − costos de terceros − comisiones.
// La comisión % se calcula sobre el subtotal (venta antes de descuento e IVA).
function resolveCommission(c, base) {
  return c.kind === 'percent' ? base * (Number(c.value) || 0) / 100 : (Number(c.value) || 0);
}
function computeMargin(items, commissions, discountType, discountValue) {
  const { subtotal, afterDisc } = computeTotals(items, discountType, discountValue, IVA_PCT);
  const cost = items.filter(i => !i.is_header)
    .reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.cost) || 0), 0);
  const commission = (commissions || []).reduce((s, c) => s + resolveCommission(c, subtotal), 0);
  const margin = afterDisc - cost - commission;
  // Sin venta neta el % es indefinido (no 0): se distingue en el render.
  const marginPct = afterDisc > 0 ? (margin / afterDisc) * 100 : null;
  return { revenue: afterDisc, cost, commission, margin, marginPct, subtotal };
}

// Sin decimales salvo que el valor los tenga (nunca se inventan ni se recortan)
function fmt(n, currency = 'UYU') {
  const v = Number(n) || 0;
  return new Intl.NumberFormat('es-UY', {
    style: 'currency', currency,
    minimumFractionDigits: 0, maximumFractionDigits: Number.isInteger(v) ? 0 : 2,
  }).format(v);
}

const equipmentKey = name => String(name || '').trim().toLocaleLowerCase('es-UY');

function inclusiveDates(start, end) {
  if (!start || !end || start > end) return [];
  const dates = [];
  const cursor = new Date(`${start}T12:00:00Z`);
  const limit = new Date(`${end}T12:00:00Z`);
  while (cursor <= limit && dates.length < 31) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function availabilityEventLabel(event) {
  const date = String(event?.date || '').slice(0, 10);
  const displayDate = date ? `${date.slice(8, 10)}/${date.slice(5, 7)}` : '';
  const time = String(event?.win_start || '').match(/(?:T|\s)(\d{2}:\d{2})/)?.[1];
  return `${event?.name || 'otro evento'}${displayDate ? ` (${displayDate}${time ? ` ${time}` : ''})` : ''}`;
}

function formatDate(str) {
  if (!str) return '';
  const d = (str || '').split('T')[0];
  const [y, m, dd] = d.split('-');
  return `${dd}/${m}/${y}`;
}

function formatWeekday(date) {
  if (!date) return '';
  const value = new Intl.DateTimeFormat('es-UY', { weekday: 'long' })
    .format(new Date(`${String(date).split('T')[0]}T12:00:00`));
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function DatePickerField({ value, onChange, title }) {
  const displayValue = value ? `${formatWeekday(value)}, ${formatDate(value)}` : 'dd/mm/aaaa';
  return (
    <label className="qp-date-picker" title={title}>
      <span className={`qp-date-picker-value${value ? '' : ' is-placeholder'}`}>{displayValue}</span>
      <svg className="qp-date-picker-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
      <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} aria-label={title} />
    </label>
  );
}

function normalizeQuoteTime(value) {
  const normalized = parseTime(value);
  if (!normalized) return '';
  const match = normalized.match(/^(\d{2}):(\d{2})$/);
  if (!match) return value.trim();
  const [, hours, minutes] = match;
  return Number(hours) <= 23 && Number(minutes) <= 59 ? normalized : '';
}

// Suma n días a una fecha 'YYYY-MM-DD' sin problemas de huso horario.
function addDaysToDateStr(dateStr, n) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().split('T')[0];
}

// "de mañana" — el evento arranca antes del mediodía.
function relativeTime(str) {
  if (!str) return '';
  const d = new Date(str);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'ahora';
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'ayer';
  if (diffD < 7) return `hace ${diffD} días`;
  return formatDate(str);
}

// ── Draft helpers ──────────────────────────────────────────────────────────────

let _kc = 0;
const makeKey = () => `k${++_kc}`;

function makeDraftHeader(section) {
  return { _key: makeKey(), section, description: section, quantity: 0, unit_price: 0, discount: 0, is_header: true, note: '', materials: [] };
}
function makeDraftItem(section) {
  return { _key: makeKey(), section, description: '', quantity: 1, unit_price: 0, discount: 0, is_header: false, note: '', materials: [], supplier_id: null, supplier_name: null, cost: '' };
}
// Comisión a un tercero (lugar/agencia). Por defecto un % del subtotal.
function makeDraftCommission() {
  return { _key: makeKey(), supplier_id: null, supplier_name: null, description: '', kind: 'percent', value: '' };
}
function initItems(existing) {
  // NUMERIC de Postgres llega como string ("1500.00") — se normaliza con
  // Number para que los inputs no muestren decimales que nadie escribió.
  if (existing?.length) return existing.map(it => ({
    ...it,
    _key: makeKey(),
    quantity: Number(it.quantity) || 0,
    unit_price: Number(it.unit_price) || 0,
    discount: Number(it.discount) || 0,
    // '' = sin costo cargado; se distingue de 0 para no mostrar un costo inventado.
    cost: it.cost == null || it.cost === '' ? '' : Number(it.cost),
    materials: (it.materials || []).map(m => ({ ...m, quantity: Number(m.quantity) || 1 })),
  }));
  return [makeDraftHeader('General'), makeDraftItem('General')];
}
function initCommissions(existing) {
  return (Array.isArray(existing) ? existing : []).map(c => ({
    ...c,
    _key: makeKey(),
    kind: c.kind === 'fixed' ? 'fixed' : 'percent',
    value: c.value == null || c.value === '' ? '' : Number(c.value),
  }));
}

// ── EventDays component ────────────────────────────────────────────────────────
// Al escribir la fecha del día 1, los días posteriores que todavía no fueron
// tocados a mano (day.auto !== false) siguen sugiriendo el día siguiente.

function EventDays({ days, onDaysChange }) {
  const updateDate = (i, value) => {
    let next = days.map((d, idx) => idx === i
      ? { ...d, date: value, auto: idx === 0 ? d.auto : false }
      : d
    );
    if (i === 0) {
      next = next.map((d, idx) => (idx === 0 || d.auto === false)
        ? d
        : { ...d, date: value ? addDaysToDateStr(value, idx) : '' }
      );
    }
    onDaysChange(next);
  };
  const updateTime = (i, k, v) => onDaysChange(days.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const remove = i => onDaysChange(days.filter((_, idx) => idx !== i));
  const add = () => {
    const base = days[0]?.date;
    const nextDate = base ? addDaysToDateStr(base, days.length) : '';
    // El día nuevo hereda el horario del día anterior (suele repetirse).
    const prev = days[days.length - 1] || {};
    onDaysChange([...days, { date: nextDate, startTime: prev.startTime || '', endTime: prev.endTime || '', auto: true }]);
  };

  return (
    <div className="qp-field qp-col2">
      <div className="qp-days">
        {days.map((d, i) => (
          <div key={i} className="qp-day-row">
            <span className="qp-day-label">Día {i + 1}</span>
            <DatePickerField
              value={d.date || ''}
              onChange={value => updateDate(i, value)}
              title={`Fecha del día ${i + 1}`}
            />
            <input className="form-input inline-mono" style={{ fontSize: 13 }} placeholder="HH:MM" value={d.startTime || ''} onChange={e => updateTime(i, 'startTime', e.target.value)} />
            <input className="form-input inline-mono" style={{ fontSize: 13 }} placeholder="HH:MM" value={d.endTime || ''} onChange={e => updateTime(i, 'endTime', e.target.value)} />
            {i === days.length - 1 && (
              <button type="button" className="qp-day-add" onClick={add} title="Agregar día">+</button>
            )}
            {days.length > 1 && (
              <button type="button" className="qp-day-remove" onClick={() => remove(i)} title="Quitar día">✕</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Fases operativas ──────────────────────────────────────────────────────────
// Armado y desarmado determinan la ventana real de ocupación de los equipos.
// Son internos: nunca aparecen en la cotización que recibe el cliente.

function OperationPanel({ label, operation, onChange, note }) {
  const update = (k, v) => onChange({ ...operation, [k]: v });

  return (
    <div className="qp-operation-panel" onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}>
      <span className="qp-operation-label">{label}</span>
      <div className="qp-operation-row">
        <DatePickerField
          value={operation.date || ''}
          onChange={value => update('date', value)}
          title={`Fecha de ${label.toLowerCase()}`}
        />
        <input className="form-input inline-mono" placeholder="Inicio" title="Inicio (HH:MM)"
          inputMode="numeric"
          value={operation.startTime || ''}
          onChange={e => update('startTime', e.target.value)}
          onBlur={e => update('startTime', normalizeQuoteTime(e.target.value))} />
        <input className="form-input inline-mono" placeholder="Fin" title="Fin (HH:MM)"
          inputMode="numeric"
          value={operation.endTime || ''}
          onChange={e => update('endTime', e.target.value)}
          onBlur={e => update('endTime', normalizeQuoteTime(e.target.value))} />
      </div>
      <span className={`qp-operation-note${operation.date ? ' is-set' : ''}`}>
        {operation.date ? note : 'Sin fecha — no restringe equipos'}
      </span>
    </div>
  );
}

// ── EquipStrip ─────────────────────────────────────────────────────────────────
// Tira compacta de equipo de depósito por ítem: reservas + sugerencias IA +
// búsqueda inline. Solo producción — nunca se imprime (print: .qp-equip oculto).

function EquipStrip({ itemKey, materials, inventoryNames, availabilityByName, demandByName, onChange, suggestions = [], suggesting, onAccept, onClearSuggestions }) {
  const [query, setQuery] = useState('');
  const [quantityToSelect, setQuantityToSelect] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const quantityInputs = useRef(new Map());
  const searchRef = useRef(null);

  const availabilityFor = (name, extraDemand = 0) => {
    const data = availabilityByName[equipmentKey(name)];
    if (!data) return { tone: 'unknown', available: null, label: 'Sin fechas para calcular disponibilidad' };
    const requested = (Number(demandByName[equipmentKey(name)]) || 0) + extraDemand;
    const available = Number(data.available) || 0;
    const collisions = (data.events || []).slice(0, 2).map(availabilityEventLabel);
    const reason = collisions.length ? ` Coincide con ${collisions.join(' y ')}.` : '';
    if (available < requested) return { tone: 'danger', available, label: `No alcanza: ${available} libre${available === 1 ? '' : 's'} el ${data.bottleneck_date}.${reason}` };
    if (available === requested) return { tone: 'warning', available, label: `Justo: ${available} libre${available === 1 ? '' : 's'} el ${data.bottleneck_date}.${reason}` };
    return { tone: 'ok', available, label: `${available} libre${available === 1 ? '' : 's'} el ${data.bottleneck_date}` };
  };

  const matchingInventory = query.trim()
    ? inventoryNames.filter(item => equipmentKey(item.name).includes(equipmentKey(query))).slice(0, 8)
    : [];

  useEffect(() => {
    if (!quantityToSelect) return;
    const input = quantityInputs.current.get(quantityToSelect);
    if (!input) return;
    input.focus();
    input.select();
    setQuantityToSelect(null);
  }, [materials, quantityToSelect]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const close = event => {
      if (!searchRef.current?.contains(event.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [searchOpen]);

  const addMaterial = name => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const existing = materials.find(m => m.item_name.toLowerCase() === trimmed.toLowerCase());
    onChange(existing
      ? materials.map(m => m === existing ? { ...m, quantity: Math.max(1, Number(m.quantity) || 1) + 1 } : m)
      : [...materials, { item_name: trimmed, quantity: 1 }]
    );
    setQuantityToSelect(existing?.item_name || trimmed);
    setQuery('');
    setSearchOpen(false);
  };

  const updateQty = (idx, qty) => onChange(materials.map((m, i) => i === idx ? { ...m, quantity: qty } : m));
  const normalizeQty = idx => updateQty(idx, Math.max(1, Number(materials[idx]?.quantity) || 1));
  const stepQty = (idx, delta) => {
    const current = Math.max(1, Number(materials[idx]?.quantity) || 1);
    updateQty(idx, Math.max(1, current + delta));
  };
  const removeMaterial = idx => onChange(materials.filter((_, i) => i !== idx));

  return (
    // Enter en cantidades/búsqueda no debe disparar el submit del form (= Enviar).
    <div className="qp-equip" title="Equipo de depósito — interno, no va al PDF."
      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}>
      <div className="qp-equip-list">
        {materials.map((m, i) => (
          <div key={`r-${i}`} className="qp-equip-item">
            <label className="qp-equip-quantity" title="Cantidad">
              <input
                type="number" min="1" step="1"
                ref={node => {
                  if (node) quantityInputs.current.set(m.item_name, node);
                  else quantityInputs.current.delete(m.item_name);
                }}
                value={m.quantity == null ? 1 : m.quantity}
                onChange={e => updateQty(i, e.target.value)}
                onBlur={() => normalizeQty(i)}
              />
            </label>
            <span className="qp-equip-name" title={m.item_name}>
              <span className={`qp-availability-dot is-${availabilityFor(m.item_name).tone}`} title={availabilityFor(m.item_name).label} />
              {m.item_name}
            </span>
            <div className="qp-equip-actions">
              <div className="qp-equip-stepper" aria-label={`Cantidad de ${m.item_name}`}>
                <button type="button" onClick={() => stepQty(i, -1)} disabled={Number(m.quantity) <= 1} title="Restar una unidad">−</button>
                <button type="button" onClick={() => stepQty(i, 1)} title="Sumar una unidad">+</button>
              </div>
              <button type="button" className="qp-equip-remove" onClick={() => removeMaterial(i)} title="Quitar equipo">✕</button>
            </div>
          </div>
        ))}
      </div>
      {(suggestions.length > 0 || suggesting) && (
        <div className="qp-equip-suggestions">
          <div className="qp-equip-suggestions-head">
            <span>Propuesta de IA</span>
            {suggestions.length > 0 && <button type="button" onClick={() => onClearSuggestions?.(itemKey)}>Descartar</button>}
          </div>
          {suggestions.map(name => (
            <button key={`s-${name}`} type="button" className="qp-equip-suggestion" onClick={() => { setQuantityToSelect(name); onAccept(name); }} title={availabilityFor(name, 1).label}>
              <span className="qp-equip-suggestion-name">
                <span className={`qp-availability-dot is-${availabilityFor(name, 1).tone}`} />
                {name}
              </span>
              <span className="qp-equip-suggestion-add">Agregar</span>
            </button>
          ))}
          {suggesting && <span className="qp-ai-loading">Analizando equipos…</span>}
        </div>
      )}
      <div className="qp-equip-add" ref={searchRef}>
        <input
          className="qp-equip-search"
          placeholder="Buscar y agregar equipo…"
          value={query}
          onChange={e => { setQuery(e.target.value); setSearchOpen(!!e.target.value.trim()); }}
          onFocus={() => { if (query.trim()) setSearchOpen(true); }}
          onKeyDown={e => {
            if (e.key === 'Escape') { setSearchOpen(false); e.currentTarget.blur(); }
            if (e.key === 'Enter' && matchingInventory[0]) { e.preventDefault(); addMaterial(matchingInventory[0].name); }
          }}
        />
        {searchOpen && (
          <div className="qp-equip-search-menu">
            {matchingInventory.map(item => {
              const availability = availabilityFor(item.name, 1);
              return (
                <button key={item.name} type="button" onMouseDown={e => { e.preventDefault(); addMaterial(item.name); }} title={availability.label}>
                  <span className={`qp-availability-dot is-${availability.tone}`} />
                  <span>{item.name}</span>
                  <small>{availability.tone === 'unknown' ? 'Sin fecha' : `${availability.available} libres`}</small>
                </button>
              );
            })}
            {!matchingInventory.length && <span className="qp-equip-search-empty">Sin equipos coincidentes</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ItemsEditor ────────────────────────────────────────────────────────────────

// Emite pares de celdas [hoja | carril] directamente en la grilla del documento
// (.qp-doc): cada ítem de la hoja queda ALINEADO con su equipo de depósito en
// el carril derecho. El PDF se genera programáticamente y jamás incluye el carril.
function configurationToResources(configuration) {
  return (configuration?.resources || []).map((resource, index) => ({
    source_configuration_resource_id: resource.id,
    source: 'configuration',
    resource_type: resource.resource_type,
    article_type_id: resource.article_type_id || null,
    rack_id: resource.rack_id || null,
    access_pack_id: resource.access_pack_id || null,
    resource_name: resource.resource_name,
    quantity: Number(resource.quantity) || 1,
    note: resource.note || null,
    sort_order: resource.sort_order ?? index,
  }));
}

function ItemsEditor({ items, products, suppliers, currency, inventoryNames, availabilityByName, demandByName, onChange, onAutoSuggest, onAcceptSuggestion, onClearSuggestions, suggestingKeys, onBulkSuggest, bulkSuggesting }) {
  const sections = [...new Set(items.filter(i => i.is_header).map(i => i.section))];
  const update = (key, patch) => onChange(items.map(it => it._key === key ? { ...it, ...patch } : it));
  const remove = key => onChange(items.filter(it => it._key !== key));

  const renameSection = (oldName, newName) => {
    const n = newName.trim();
    if (!n || n === oldName) return false;
    // Chocar con otra sección fusionaría las dos sin vuelta atrás (headers
    // duplicados, subtotales y zonas de equipos mezcladas).
    if (sections.some(s => s !== oldName && s.toLowerCase() === n.toLowerCase())) {
      alert(`Ya existe una sección "${n}"`);
      return false;
    }
    onChange(items.map(it => it.section === oldName ? { ...it, section: n, description: it.is_header ? n : it.description } : it));
    return true;
  };

  const addItem = section => {
    const lastIdx = items.reduce((acc, it, idx) => it.section === section ? idx : acc, -1);
    const next = [...items];
    next.splice(lastIdx + 1, 0, makeDraftItem(section));
    onChange(next);
  };

  const addSection = () => {
    const name = `Sección ${sections.length + 1}`;
    onChange([...items, makeDraftHeader(name), makeDraftItem(name)]);
  };

  const removeSection = section => {
    if (sections.length <= 1) { alert('Tiene que quedar al menos una sección.'); return; }
    const withContent = items.some(it => !it.is_header && it.section === section &&
      ((it.description || '').trim() || Number(it.unit_price) > 0));
    if (withContent && !window.confirm(`¿Eliminar la sección "${section}" con sus ítems?`)) return;
    onChange(items.filter(it => it.section !== section));
  };

  // ── Drag & drop: ordenar ítems (dentro y entre secciones) y secciones ──────
  const dragRef = useRef(null);                 // { type: 'item'|'section', key?, section? }
  const [dropHint, setDropHint] = useState(null); // { id, below } — id: _key o 'sec:<nombre>'

  const moveItemTo = (dragKey, targetKey, below) => {
    if (dragKey === targetKey) return;
    const next = [...items];
    const from = next.findIndex(it => it._key === dragKey);
    if (from === -1) return;
    const [dragged] = next.splice(from, 1);
    const ti = next.findIndex(it => it._key === targetKey);
    if (ti === -1) return;
    next.splice(below ? ti + 1 : ti, 0, { ...dragged, section: next[ti].section });
    onChange(next);
  };

  const moveItemToSectionStart = (dragKey, section) => {
    const next = [...items];
    const from = next.findIndex(it => it._key === dragKey);
    if (from === -1) return;
    const [dragged] = next.splice(from, 1);
    const hi = next.findIndex(it => it.is_header && it.section === section);
    next.splice(hi + 1, 0, { ...dragged, section });
    onChange(next);
  };

  const moveSectionTo = (dragSection, targetSection, below) => {
    if (dragSection === targetSection) return;
    const block = items.filter(it => it.section === dragSection);
    const rest = items.filter(it => it.section !== dragSection);
    let idx = rest.findIndex(it => it.is_header && it.section === targetSection);
    if (idx === -1) return;
    if (below) {
      while (idx < rest.length && rest[idx].section === targetSection) idx++;
    }
    rest.splice(idx, 0, ...block);
    onChange(rest);
  };

  const startDrag = (e, payload) => {
    dragRef.current = payload;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ''); // Firefox necesita setData para arrancar
    const band = e.currentTarget.closest('.qp-sheet');
    if (band) e.dataTransfer.setDragImage(band, 24, 18);
  };
  const endDrag = () => { dragRef.current = null; setDropHint(null); };

  const overItem = (e, item) => {
    const d = dragRef.current;
    if (!d || d.type !== 'item' || d.key === item._key) return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    setDropHint({ id: item._key, below: e.clientY > r.top + r.height / 2 });
  };
  const overSection = (e, section) => {
    const d = dragRef.current;
    if (!d || (d.type === 'section' && d.section === section)) return;
    e.preventDefault();
    if (d.type === 'item') {
      setDropHint({ id: `sec:${section}`, below: true }); // cae como primer ítem
    } else {
      const r = e.currentTarget.getBoundingClientRect();
      setDropHint({ id: `sec:${section}`, below: e.clientY > r.top + r.height / 2 });
    }
  };
  // below se recalcula del propio evento de drop — dropHint es solo visual y
  // puede llegar un render atrasado en soltadas rápidas.
  const isBelow = e => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY > r.top + r.height / 2;
  };
  const dropOnItem = (e, item) => {
    e.preventDefault();
    const d = dragRef.current;
    const below = isBelow(e);
    endDrag();
    if (d?.type === 'item') moveItemTo(d.key, item._key, below);
  };
  const dropOnSection = (e, section) => {
    e.preventDefault();
    const d = dragRef.current;
    const below = isBelow(e);
    endDrag();
    if (!d) return;
    if (d.type === 'item') moveItemToSectionStart(d.key, section);
    else moveSectionTo(d.section, section, below);
  };
  const hintCls = id => dropHint?.id === id ? (dropHint.below ? ' qp-drop-below' : ' qp-drop-above') : '';

  return (
    <>
      {/* Banda: cabecera de la tabla | título del carril + IA bulk */}
      <div className="qp-sheet qp-commercial-sheet">
        <div className="qp-table-head">
          <span>Descripción</span>
          <span>Cant.</span>
          <span>Precio</span>
          <span>Dcto</span>
          <span>Total</span>
          <span />
        </div>
      </div>
      <div className="qp-margin qp-rail-head">
        {onBulkSuggest && (
          <button type="button" className="qp-bulk-ai" onClick={onBulkSuggest} disabled={bulkSuggesting} title="Analiza la cotización y propone equipos de depósito">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z"/>
            </svg>
            {bulkSuggesting ? 'Sugiriendo equipos…' : 'Sugerir equipos con IA'}
          </button>
        )}
      </div>

      {sections.map((section, sectionIndex) => {
        const sectionItems = items.filter(i => i.section === section && !i.is_header);
        const sectionSubtotal = sectionItems.reduce((total, item) => total + lineTotal(item), 0);
        const isLastSection = sectionIndex === sections.length - 1;
        return (
          <React.Fragment key={section}>
            <div
              className={`qp-sheet qp-section-sheet${hintCls(`sec:${section}`)}`}
              onDragOver={e => overSection(e, section)}
              onDrop={e => dropOnSection(e, section)}
            >
              <div className="qp-section-row">
                <span
                  className="qp-drag-handle"
                  title="Arrastrar para ordenar la sección"
                  draggable
                  onDragStart={e => startDrag(e, { type: 'section', section })}
                  onDragEnd={endDrag}
                >⋮⋮</span>
                <input
                  className="qp-section-name-input"
                  defaultValue={section}
                  onBlur={e => { if (!renameSection(section, e.target.value)) e.target.value = section; }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                />
                <button type="button" className="qp-section-additem" onClick={() => addItem(section)}>
                  + ítem
                </button>
                <button type="button" className="btn btn-icon btn-ghost qp-section-del"
                  title="Eliminar sección"
                  onClick={() => removeSection(section)}
                >✕</button>
              </div>
            </div>
            <div className="qp-margin qp-section-margin" />

            {sectionItems.map(item => {
              const selectedProduct = products.find(product => String(product.id) === String(item.product_id));
              const configurations = selectedProduct?.configurations || [];
              return (
              <React.Fragment key={item._key}>
                <div
                  className={`qp-sheet qp-item-sheet${hintCls(item._key)}`}
                  onDragOver={e => overItem(e, item)}
                  onDrop={e => dropOnItem(e, item)}
                >
                  <div className="qp-item-band">
                    <span
                      className="qp-drag-handle"
                      title="Arrastrar para ordenar"
                      draggable
                      onDragStart={e => startDrag(e, { type: 'item', key: item._key })}
                      onDragEnd={endDrag}
                    >⋮⋮</span>
                    <div className="qp-item-main">
                      {/* Los productos son hijos de la lista maestra (Ventas → Productos):
                          buscá tipeando — nunca texto libre. */}
                      <SearchSelect
                        value={item.product_id ? String(item.product_id) : (item.description ? '__legacy__' : null)}
                        options={products.map(p => ({ key: p.id, label: p.name, data: p }))}
                        extraOption={!item.product_id && item.description
                          ? { key: '__legacy__', label: item.description }
                          : undefined}
                        onSelect={o => {
                          if (o?.key === '__legacy__') return;
                          const p = o?.data || null;
                          const defaultConfiguration = p?.configurations?.find(configuration => configuration.is_default) || p?.configurations?.[0] || null;
                          update(item._key, {
                            product_id: p ? p.id : null,
                            product_configuration_id: defaultConfiguration?.id || null,
                            description: p ? p.name : '',
                            unit_price: defaultConfiguration?.unit_price ?? (p?.unit_price != null ? p.unit_price : item.unit_price),
                            resources: defaultConfiguration ? configurationToResources(defaultConfiguration) : [],
                          });
                          // Al elegir producto, la IA sugiere el equipo del depósito automáticamente.
                          if (p) onAutoSuggest?.(item._key, p.name, p.description);
                        }}
                        placeholder="Buscar producto…"
                        clearLabel="Quitar producto"
                      />
                      <input className="form-input qp-num" type="number" min="0" step="any"
                        title="Cantidad"
                        value={item.quantity}
                        onChange={e => update(item._key, { quantity: e.target.value })}
                      />
                      <input className="form-input qp-num" type="number" min="0" step="any"
                        title="Precio unitario"
                        value={item.unit_price}
                        onChange={e => update(item._key, { unit_price: e.target.value })}
                      />
                      <input className="form-input qp-num" type="number" min="0" max="100" step="1"
                        title="Descuento %"
                        value={item.discount}
                        onChange={e => update(item._key, { discount: Math.min(100, Number(e.target.value) || 0) })}
                      />
                      <span className="qp-item-total">{fmt(lineTotal(item), currency)}</span>
                      <button type="button" className="btn btn-icon btn-ghost"
                        style={{ color: 'var(--text-mute, var(--text-dim))', minHeight: 24, minWidth: 24 }}
                        onClick={() => remove(item._key)}
                        title="Quitar ítem"
                      >✕</button>
                    </div>
                    {configurations.length > 0 && (
                      <label className="qp-configuration-select">
                        <span>Configuración</span>
                        <select
                          className="form-input"
                          value={item.product_configuration_id || ''}
                          onChange={event => {
                            const configuration = configurations.find(current => String(current.id) === event.target.value) || null;
                            update(item._key, {
                              product_configuration_id: configuration?.id || null,
                              unit_price: configuration?.unit_price ?? (selectedProduct?.unit_price ?? item.unit_price),
                              resources: configuration ? configurationToResources(configuration) : [],
                            });
                          }}
                        >
                          <option value="">Sin configuración</option>
                          {configurations.map(configuration => <option key={configuration.id} value={configuration.id}>{configuration.name}</option>)}
                        </select>
                      </label>
                    )}
                    {selectedProduct?.description && (
                      <span className="qp-product-description">{selectedProduct.description}</span>
                    )}
                    <input className="qp-note-input"
                      placeholder="Detalle opcional (va al PDF)…"
                      value={item.note || ''}
                      onChange={e => update(item._key, { note: e.target.value || null })}
                    />
                    {/* Costo de tercero — interno, no aparece en el PDF del cliente.
                        Sirve para reventa (generador, DJ externo): venta al cliente
                        arriba, lo que le pagás al proveedor acá. */}
                    <div className="qp-item-internal" title="Interno — no va al PDF del cliente">
                      <span className="qp-item-internal-tag">Tercero</span>
                      <div className="qp-item-internal-supplier">
                        <SearchSelect
                          value={item.supplier_id ? String(item.supplier_id) : null}
                          options={suppliers.map(s => ({ key: s.id, label: s.company ? `${s.name} · ${s.company}` : s.name, data: s }))}
                          extraOption={item.supplier_id && !suppliers.some(s => String(s.id) === String(item.supplier_id))
                            ? { key: String(item.supplier_id), label: `${item.supplier_name || `Proveedor #${item.supplier_id}`} (inactivo)` }
                            : undefined}
                          onSelect={o => update(item._key, { supplier_id: o?.data ? o.data.id : null, supplier_name: o?.data?.name || null })}
                          placeholder="Proveedor (opcional)…"
                          clearLabel="Quitar proveedor"
                        />
                      </div>
                      <input className="form-input qp-num" type="number" min="0" step="any"
                        title="Costo unitario que pagás al proveedor"
                        placeholder="Costo unit."
                        value={item.cost}
                        onChange={e => update(item._key, { cost: e.target.value })}
                      />
                      <span className="qp-item-internal-total" title="Costo total de la línea">
                        {(Number(item.cost) || 0) > 0 ? fmt((Number(item.quantity) || 0) * (Number(item.cost) || 0), currency) : '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="qp-margin qp-item-margin">
                  <EquipStrip
                    itemKey={item._key}
                    materials={item.materials || []}
                    inventoryNames={inventoryNames}
                    availabilityByName={availabilityByName}
                    demandByName={demandByName}
                    onChange={materials => update(item._key, { materials })}
                    suggestions={item._suggestions || []}
                    suggesting={suggestingKeys?.includes(item._key)}
                    onAccept={name => onAcceptSuggestion?.(item._key, name)}
                    onClearSuggestions={onClearSuggestions}
                  />
                </div>
              </React.Fragment>
              );
            })}
            <div className={`qp-sheet qp-section-total-sheet${isLastSection ? ' qp-section-total-last' : ''}`}>
              <span>Subtotal {section}</span>
              <strong>{fmt(sectionSubtotal, currency)}</strong>
            </div>
            <div className={`qp-margin qp-section-total-margin${isLastSection ? ' qp-section-total-last' : ''}`} />
          </React.Fragment>
        );
      })}

      <div className="qp-sheet qp-add-section-sheet">
        <button type="button" className="qp-add-section" onClick={addSection}>
          + Agregar sección / sub-lugar
        </button>
      </div>
      <div className="qp-margin" />
    </>
  );
}

// ── Totals ─────────────────────────────────────────────────────────────────────

function Totals({ items, discountType, discountValue, currency, onChange }) {
  const { subtotal, discAmt, afterDisc, taxAmt, total } = computeTotals(items, discountType, discountValue, IVA_PCT);
  return (
    <div className="qp-totals">
      <div className="qp-totals-box">
        <div className="qp-totals-body">
          <div className="qp-total-row">
            <span className="qp-total-lbl">Subtotal</span>
            <span className="qp-total-val">{fmt(subtotal, currency)}</span>
          </div>
          <div className="qp-total-row">
            <span className="qp-total-lbl">
              Descuento
              <div className="qp-disc-type">
                <button type="button" className={discountType === 'PERCENT' ? 'active' : ''} onClick={() => onChange('discountType', 'PERCENT')}>%</button>
                <button type="button" className={discountType === 'AMOUNT' ? 'active' : ''} onClick={() => onChange('discountType', 'AMOUNT')}>$</button>
              </div>
              <input className="form-input" type="number" min="0" step="any"
                value={discountValue} onChange={e => onChange('discountValue', e.target.value)}
                style={{ width: 64, textAlign: 'right', fontSize: 12 }}
              />
            </span>
            <span className="qp-total-val" style={{ color: 'var(--text-dim)' }}>−{fmt(discAmt, currency)}</span>
          </div>
          {discAmt > 0.01 && (
            <div className="qp-total-row" style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
              <span className="qp-total-lbl">Subtotal con descuento</span>
              <span className="qp-total-val">{fmt(afterDisc, currency)}</span>
            </div>
          )}
          <div className="qp-total-row">
            <span className="qp-total-lbl">IVA ({IVA_PCT}%)</span>
            <span className="qp-total-val" style={{ color: 'var(--text-dim)' }}>{fmt(taxAmt, currency)}</span>
          </div>
        </div>
        <div className="qp-totals-foot">
          <span>Total</span>
          <span className="qp-grand-total">{fmt(total, currency)}</span>
        </div>
      </div>
    </div>
  );
}

// ── Interno: comisiones + margen ─────────────────────────────────────────────────
// Todo esto es para el equipo — NUNCA se incluye en el PDF del cliente.
function InternalPanel({ items, commissions, suppliers, discountType, discountValue, currency, onChange }) {
  const { revenue, cost, commission, margin, marginPct, subtotal } = computeMargin(items, commissions, discountType, discountValue);
  const update = (key, patch) => onChange(commissions.map(c => c._key === key ? { ...c, ...patch } : c));
  const remove = key => onChange(commissions.filter(c => c._key !== key));
  const add = () => onChange([...commissions, makeDraftCommission()]);
  const marginTone = margin < 0 ? 'is-neg' : revenue <= 0 ? '' : marginPct < 15 ? 'is-low' : 'is-ok';
  const supplierOptions = suppliers.map(s => ({ key: s.id, label: s.company ? `${s.name} · ${s.company}` : s.name, data: s }));

  return (
    <div className="qp-internal">
      <div className="qp-internal-head">
        <span className="qp-internal-title">Interno · comisiones y margen</span>
        <span className="qp-internal-note">No aparece en el PDF del cliente</span>
      </div>
      <div className="qp-internal-body">
        <div className="qp-commissions">
          <div className="qp-commissions-head">
            <span>Comisiones a terceros (lugar / agencia)</span>
            <button type="button" className="qp-comm-add" onClick={add}>+ Agregar comisión</button>
          </div>
          {commissions.length === 0 && <div className="qp-commissions-empty">Sin comisiones cargadas.</div>}
          {commissions.map(c => (
            <div key={c._key} className="qp-commission-row">
              <input className="form-input qp-comm-desc"
                placeholder="Concepto (ej. comisión al lugar)…"
                value={c.description || ''}
                onChange={e => update(c._key, { description: e.target.value })}
              />
              <div className="qp-comm-supplier">
                <SearchSelect
                  value={c.supplier_id ? String(c.supplier_id) : null}
                  options={supplierOptions}
                  extraOption={c.supplier_id && !suppliers.some(s => String(s.id) === String(c.supplier_id))
                    ? { key: String(c.supplier_id), label: `${c.supplier_name || `Proveedor #${c.supplier_id}`} (inactivo)` }
                    : undefined}
                  onSelect={o => update(c._key, { supplier_id: o?.data ? o.data.id : null, supplier_name: o?.data?.name || null })}
                  placeholder="Proveedor (opcional)…"
                  clearLabel="Quitar proveedor"
                />
              </div>
              <div className="qp-comm-kind" title={c.kind === 'percent' ? '% sobre el subtotal (venta sin IVA)' : 'Monto fijo'}>
                <button type="button" className={c.kind === 'percent' ? 'active' : ''} onClick={() => update(c._key, { kind: 'percent' })}>%</button>
                <button type="button" className={c.kind === 'fixed' ? 'active' : ''} onClick={() => update(c._key, { kind: 'fixed' })}>$</button>
              </div>
              <input className="form-input qp-num" type="number" min="0" step="any"
                title={c.kind === 'percent' ? '% sobre el subtotal' : 'Monto fijo'}
                value={c.value}
                onChange={e => update(c._key, { value: e.target.value })}
              />
              <span className="qp-comm-amount">{fmt(resolveCommission(c, subtotal), currency)}</span>
              <button type="button" className="btn btn-icon btn-ghost qp-comm-del" onClick={() => remove(c._key)} title="Quitar comisión">✕</button>
            </div>
          ))}
        </div>
        <div className="qp-margin-box">
          <div className="qp-margin-row">
            <span>Venta neta (sin IVA)</span>
            <span>{fmt(revenue, currency)}</span>
          </div>
          <div className="qp-margin-row">
            <span>Costos de terceros</span>
            <span className="qp-margin-neg">−{fmt(cost, currency)}</span>
          </div>
          <div className="qp-margin-row">
            <span>Comisiones</span>
            <span className="qp-margin-neg">−{fmt(commission, currency)}</span>
          </div>
          <div className={`qp-margin-total ${marginTone}`}>
            <span>Margen</span>
            <span>{fmt(margin, currency)} <small>({marginPct == null ? '—' : `${marginPct.toFixed(0)}%`})</small></span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── VersionHistory sidebar ─────────────────────────────────────────────────────

function VersionHistory({ quoteId, refreshKey, isDirty, previewVersion, onPreview, onBackToCurrent, onCreateVersion, creatingVersion, onDeleteVersion, deletingVersionId }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/quotes/${quoteId}/versions`)
      .then(({ data }) => setVersions(Array.isArray(data) ? data : []))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [quoteId, refreshKey]);

  const statusOpt = s => STATUS_OPTS.find(o => o.value === s) || STATUS_OPTS[0];
  const lastN = versions[0]?.version_number || 0;

  return (
    <div className="qp-history">
      <div className="qp-history-head">
        <span className="qp-history-toggle-left">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="12 8 12 12 14 14"/><path d="M3.05 11a9 9 0 1 0 .5-4.5"/>
            <polyline points="3 3 3 7 7 7"/>
          </svg>
          Versiones{versions.length ? ` (${versions.length})` : ''}
          <span className={`qp-current-dot${isDirty ? ' dirty' : ''}`} style={{ marginLeft: 4 }} />
        </span>
        <button
          type="button"
          className="qp-history-create"
          onClick={onCreateVersion}
          disabled={creatingVersion || !!previewVersion}
          title={previewVersion ? 'Volvé a la cotización actual para crear una versión' : 'Guardar una versión del estado actual'}
        >
          {creatingVersion ? 'Guardando...' : 'Nueva versión'}
        </button>
      </div>
      <div className="qp-history-body">
        {/* La versión que estás editando ahora — late para marcar dónde estás */}
        <button
          type="button"
          className={`qp-version-item qp-version-current${!previewVersion ? ' active' : ''}`}
          onClick={onBackToCurrent}
          title={previewVersion ? 'Volver a la versión actual' : 'Estás editando esta versión'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span className={`qp-current-dot${isDirty ? ' dirty' : ''}`} />
            <span className="qp-version-num">
              {isDirty ? `v${lastN + 1} — editando` : lastN ? `Actual = v${lastN}` : 'Versión actual'}
            </span>
          </div>
          <span className="qp-version-time">
            {isDirty ? 'cambios sin enviar' : 'sin cambios pendientes'}
          </span>
        </button>

        {loading ? (
          <div className="qp-history-empty"><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : versions.length === 0 ? (
          <div className="qp-history-empty">Todavía no hay versiones.</div>
        ) : (
          versions.map(v => {
            const s = statusOpt(v.status);
            const isPreviewing = previewVersion?.id === v.id;
            return (
              <div
                key={v.id}
                className={`qp-version-item${isPreviewing ? ' previewing' : ''}`}
              >
                <button
                  type="button"
                  className="qp-version-view"
                  onClick={() => onPreview(v)}
                  title={v.has_snapshot ? `Ver la v${v.version_number}` : 'Versión sin datos para restaurar'}
                >
                  <span className="qp-version-num">v{v.version_number}{isPreviewing ? ' — viendo' : ''}</span>
                  <span className="qp-version-status" style={{ background: `${s.color}22`, color: s.color }}>
                    {v.kind === 'manual' ? 'Manual' : s.label}
                  </span>
                  <span className="qp-version-time">{relativeTime(v.created_at)}</span>
                  {v.total != null && (
                    <span className="qp-version-total">
                      {new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU', minimumFractionDigits: 0 }).format(v.total)}
                    </span>
                  )}
                </button>
                {v.kind === 'manual' && (
                  <button
                    type="button"
                    className="qp-version-delete"
                    onClick={() => onDeleteVersion(v)}
                    disabled={deletingVersionId === v.id}
                    title="Borrar versión manual"
                    aria-label={`Borrar versión ${v.version_number}`}
                  >
                    {deletingVersionId === v.id ? '...' : '×'}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function OperationalSummary({ items, onSuggestAll, suggesting }) {
  const services = items.filter(item => !item.is_header);
  const itemResources = item => [
    ...(item.resources || []),
    ...(item.materials || []),
  ];
  const plannedResources = services.reduce(
    (total, item) => total + itemResources(item).reduce((sum, resource) => sum + (Number(resource.quantity) || 1), 0),
    0
  );
  const linkedServices = services.filter(item => itemResources(item).length > 0).length;
  const needsReview = services.filter(item => item.product_id && itemResources(item).length === 0).length;

  return (
    <aside className="qp-operational-summary" aria-label="Plan operativo interno">
      <div className="qp-operational-head">
        <span>Plan operativo · interno</span>
        <small>No visible al cliente</small>
      </div>
      <div className="qp-operational-metrics">
        <div><strong>{plannedResources}</strong><span>recursos previstos</span></div>
        <div className="is-linked"><strong>{linkedServices}</strong><span>servicios vinculados</span></div>
        <div className={needsReview ? 'is-review' : ''}><strong>{needsReview}</strong><span>a revisar</span></div>
      </div>
      {needsReview > 0 ? (
        <div className="qp-operational-alert">
          <span>Falta equipo en {needsReview} servicio{needsReview === 1 ? '' : 's'}.</span>
          <button type="button" onClick={onSuggestAll} disabled={suggesting || !onSuggestAll}>
            {suggesting ? 'Buscando…' : 'Revisar depósito'}
          </button>
        </div>
      ) : (
        <div className="qp-operational-ok">Equipo asociado en los servicios cargados.</div>
      )}
    </aside>
  );
}

// ── QuotePage ──────────────────────────────────────────────────────────────────

export default function QuotePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'ventas.cotizaciones');

  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [enviado, setEnviado]     = useState(false); // brief success flash
  const [isDirty, setIsDirty]     = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [creatingVersion, setCreatingVersion] = useState(false);
  const [deletingVersionId, setDeletingVersionId] = useState(null);
  const [quote, setQuote]         = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null); // versión del historial en pantalla
  const [equipConflict, setEquipConflict] = useState(null); // 409: equipos tocados en planilla
  const [currencyModal, setCurrencyModal] = useState(null); // { to, rate, fetching }
  const [shortageNotice, setShortageNotice] = useState(null); // equipos sin unidad física al enviar
  const [suggestingKeys, setSuggestingKeys] = useState([]);
  const [bulkSuggesting, setBulkSuggesting] = useState(false);
  const suggestingRef = useRef(new Set());
  // localDirty: hay cambios locales sin autoguardar (dispara el PUT).
  // isDirty: hay cambios sin ENVIAR (needs_send del servidor — el botón late).
  const [localDirty, setLocalDirty] = useState(false);
  const dirtySeq = useRef(0);
  const sendingRef = useRef(false);
  const autosaveInFlight = useRef(null);

  // Form state
  const [title, setTitle]               = useState('');
  const [clientId, setClientId]         = useState('');
  const [lugarId, setLugarId]           = useState(null);
  const [espacioId, setEspacioId]       = useState(null);
  const [days, setDays]                 = useState([{ date: '', startTime: '', endTime: '' }]);
  const [armado, setArmado]             = useState({ date: '', startTime: '', endTime: '' });
  const [desarmado, setDesarmado]       = useState({ date: '', startTime: '', endTime: '' });
  const [businessUnit, setBusinessUnit] = useState('');
  const [eventType, setEventType]       = useState('');
  const [currency, setCurrency]         = useState('UYU');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [discountType, setDiscountType] = useState('PERCENT');
  const [discountValue, setDiscountValue] = useState('0');
  const [status, setStatus]             = useState('posible');
  const [notes, setNotes]               = useState('');
  const [items, setItems]               = useState(() => [makeDraftHeader('General'), makeDraftItem('General')]);
  const [commissions, setCommissions]   = useState([]);

  // Reference data
  const [clients, setClients]     = useState([]);
  const [products, setProducts]   = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lugares, setLugares]     = useState([]);
  const [inventoryNames, setInventoryNames] = useState([]);
  const [availabilityByName, setAvailabilityByName] = useState({});

  const availabilityDates = useMemo(() => {
    const eventDates = days.map(day => day.date).filter(value => /^\d{4}-\d{2}-\d{2}$/.test(value || '')).sort();
    const setupDate = /^\d{4}-\d{2}-\d{2}$/.test(armado.date || '') ? armado.date : null;
    const teardownDate = /^\d{4}-\d{2}-\d{2}$/.test(desarmado.date || '') ? desarmado.date : null;
    const start = setupDate || eventDates[0];
    const end = teardownDate || eventDates[eventDates.length - 1];
    return inclusiveDates(start, end);
  }, [days, armado.date, desarmado.date]);

  const demandByName = useMemo(() => items.reduce((result, item) => {
    if (item.is_header) return result;
    for (const material of item.materials || []) {
      const key = equipmentKey(material.item_name);
      if (!key) continue;
      result[key] = (result[key] || 0) + Math.max(1, Number(material.quantity) || 1);
    }
    return result;
  }, {}), [items]);

  useEffect(() => {
    if (!availabilityDates.length) {
      setAvailabilityByName({});
      return undefined;
    }
    let alive = true;
    const timeout = window.setTimeout(() => {
      api.post('/quotes/equipment-availability', { dates: availabilityDates })
        .then(response => {
          if (!alive) return;
          const normalized = Object.entries(response.data?.by_name || {}).reduce((result, [name, value]) => {
            result[equipmentKey(name)] = value;
            return result;
          }, {});
          setAvailabilityByName(normalized);
        })
        .catch(() => { if (alive) setAvailabilityByName({}); });
    }, 250);
    return () => { alive = false; window.clearTimeout(timeout); };
  }, [availabilityDates.join('|')]);

  // Mark dirty on any form change
  const dirty = useCallback(() => {
    dirtySeq.current++;
    setIsDirty(true);
    setLocalDirty(true);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, directoryRes] = await Promise.all([
        api.get(`/quotes/${id}`),
        api.get('/quotes/editor-directory'),
      ]);
      const q = qRes.data;
      const directory = directoryRes.data || {};
      setQuote(q);
      setTitle(q.title || '');
      setClientId(q.client_id ? String(q.client_id) : '');
      setLugarId(q.lugar_id || null);
      setEspacioId(q.espacio_id || null);
      const parsedDays = Array.isArray(q.days) ? q.days : (q.days ? JSON.parse(q.days) : null);
      // auto:false en días ya guardados — no pisar fechas que el usuario eligió a mano.
      setDays(parsedDays?.length
        ? parsedDays.map(d => ({ ...d, auto: false }))
        : [{ date: q.event_date ? q.event_date.split('T')[0] : '', startTime: '', endTime: '' }]
      );
      const loadedArmado = { date: q.armado_date ? q.armado_date.split('T')[0] : '', startTime: q.armado_start_time || '', endTime: q.armado_end_time || '' };
      const loadedDesarmado = { date: q.teardown_date ? q.teardown_date.split('T')[0] : '', startTime: q.teardown_start || '', endTime: q.teardown_end || '' };
      setArmado(loadedArmado);
      setDesarmado(loadedDesarmado);
      setBusinessUnit(q.business_unit || '');
      setEventType(q.event_type || '');
      setCurrency(q.currency || 'UYU');
      setPaymentTerms(q.payment_terms || '');
      setDiscountType(q.discount_type || 'PERCENT');
      setDiscountValue(String(Number(q.discount_value) || 0));
      setStatus(q.status || 'posible');
      setNotes(q.notes || '');
      setItems(initItems(q.items));
      setCommissions(initCommissions(q.commissions));
      setClients(Array.isArray(directory.clients) ? directory.clients : []);
      setProducts(Array.isArray(directory.products) ? directory.products : []);
      setSuppliers(Array.isArray(directory.suppliers) ? directory.suppliers : []);
      setLugares(Array.isArray(directory.lugares) ? directory.lugares : []);
      setInventoryNames(Array.isArray(directory.inventory_names) ? directory.inventory_names : []);
      // El estado "para enviar" vive en el servidor: si hubo cambios después
      // del último Enviar (incluso de otra sesión), el botón sigue latiendo.
      // Ojo: abrir una cotización needs_send NO debe autoguardar el formulario
      // recién cargado (pisaría ediciones más nuevas de otro usuario).
      setIsDirty(!!q.needs_send);
      setLocalDirty(false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Autoguardado del borrador (debounced): los cambios persisten sin Enviar.
  // El PUT actualiza updated_at → needs_send queda true en el servidor hasta
  // el próximo Enviar (el botón sigue latiendo aunque cierres y vuelvas).
  // Solo corre con cambios locales pendientes (localDirty) y se apaga tras
  // guardar — no re-PUTea en cada render ni al abrir una cotización ajena.
  useEffect(() => {
    // En vista de versión no se autoguarda nada — solo al Restaurar.
    if (!localDirty || sending || loading || previewVersion) return;
    const seq = dirtySeq.current;
    const t = setTimeout(() => {
      if (sendingRef.current) return; // Enviar en curso — él persiste todo
      autosaveInFlight.current = api.put(`/quotes/${id}`, buildPayload())
        .then(() => { if (dirtySeq.current === seq) setLocalDirty(false); })
        .catch(() => {})
        .finally(() => { autosaveInFlight.current = null; });
    }, 1200);
    return () => clearTimeout(t);
  });

  // La fecha de armado sigue a la del evento: por defecto SIEMPRE el día
  // previo. Al cambiar la fecha del día 1 el armado se recalcula al día previo
  // (aunque tuviera otra); al cargar solo se llena si estaba vacío. Borrarlo a
  // mano no lo vuelve a llenar hasta el próximo cambio de fecha.
  const prevDay0 = useRef(undefined); // undefined = todavía no inicializado
  useEffect(() => {
    if (loading) return;
    const d0 = days[0]?.date || '';
    const prev = prevDay0.current;
    prevDay0.current = d0;
    if (!d0) return;
    if (prev === undefined) {
      if (!armado.date) {
        setArmado(a => ({ ...a, date: addDaysToDateStr(d0, -1) }));
      }
    } else if (prev !== d0) {
      setArmado(a => ({ ...a, date: addDaysToDateStr(d0, -1) }));
    }
  }, [days, loading, armado.date]);

  function buildPayload() {
    return {
      title: title.trim(),
      client_id: clientId ? Number(clientId) : null,
      lugar_id: lugarId,
      espacio_id: espacioId,
      event_date: days[0]?.date || null,
      days: days.filter(d => d.date).map(({ date, startTime, endTime }) => ({ date, startTime, endTime })),
      armado_date: armado.date || null,
      armado_start_time: armado.startTime || null,
      armado_end_time: armado.endTime || null,
      teardown_date: desarmado.date || null,
      teardown_start: desarmado.startTime || null,
      teardown_end: desarmado.endTime || null,
      business_unit: businessUnit || null,
      event_type: eventType || null,
      currency,
      payment_terms: paymentTerms || null,
      tax_rate: IVA_PCT,
      discount_type: discountType,
      discount_value: parseFloat(discountValue) || 0,
      status,
      notes: notes || null,
      commissions: commissions.map((c, i) => ({
        supplier_id: c.supplier_id || null,
        description: c.description || '',
        kind: c.kind === 'fixed' ? 'fixed' : 'percent',
        value: Number(c.value) || 0,
        sort_order: i,
      })),
      items: items.map((it, i) => ({
        product_id: it.product_id || null,
        product_configuration_id: it.product_configuration_id || null,
        section: it.section,
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
        discount: Number(it.discount) || 0,
        is_header: !!it.is_header,
        note: it.note || null,
        order: i,
        supplier_id: it.is_header ? null : (it.supplier_id || null),
        cost: it.is_header || it.cost === '' || it.cost == null ? null : Number(it.cost),
        materials: (it.materials || []).map(m => ({ item_name: m.item_name, quantity: Number(m.quantity) || 1 })),
        resources: (it.resources || []).map(resource => ({
          source_configuration_resource_id: resource.source_configuration_resource_id || null,
          source: resource.source || 'manual',
          resource_type: resource.resource_type,
          article_type_id: resource.article_type_id || null,
          rack_id: resource.rack_id || null,
          access_pack_id: resource.access_pack_id || null,
          resource_name: resource.resource_name,
          quantity: Number(resource.quantity) || 1,
          note: resource.note || null,
          sort_order: resource.sort_order,
        })),
      })),
    };
  }

  // El autoguardado sigue funcionando como respaldo. Este control deja una
  // acción deliberada para quien está armando una cotización larga.
  const handleSaveDraft = async () => {
    if (sendingRef.current || previewVersion) return;
    setSavingDraft(true);
    try {
      if (autosaveInFlight.current) { try { await autosaveInFlight.current; } catch {} }
      await api.put(`/quotes/${id}`, buildPayload());
      setLocalDirty(false);
      setIsDirty(true);
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo guardar el borrador');
    } finally {
      setSavingDraft(false);
    }
  };

  // Un hito manual guarda el estado actual del presupuesto para poder volver
  // a él. A diferencia de Enviar a Agenda, no toca eventos ni reservas.
  const handleCreateVersion = async () => {
    if (sendingRef.current || previewVersion) return;
    setCreatingVersion(true);
    try {
      if (autosaveInFlight.current) { try { await autosaveInFlight.current; } catch {} }
      const payload = buildPayload();
      await api.put(`/quotes/${id}`, payload);
      const { total } = computeTotals(items, discountType, discountValue, IVA_PCT);
      await api.post(`/quotes/${id}/versions`, { snapshot: payload, total });
      setLocalDirty(false);
      setHistoryKey(k => k + 1);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo crear la versión');
    } finally {
      setCreatingVersion(false);
    }
  };

  const handleDeleteVersion = async version => {
    if (!version || version.kind !== 'manual' || deletingVersionId) return;
    if (!window.confirm(`¿Borrar la versión manual v${version.version_number}?`)) return;
    setDeletingVersionId(version.id);
    try {
      await api.delete(`/quotes/${id}/versions/${version.id}`);
      if (previewVersion?.id === version.id) await handleBackToCurrent();
      setHistoryKey(k => k + 1);
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo borrar la versión');
    } finally {
      setDeletingVersionId(null);
    }
  };

  // ── Historial de versiones: ver / restaurar ────────────────────────────────

  // Vuelca un snapshot (el payload guardado al enviar) al formulario.
  const applySnapshot = s => {
    setTitle(s.title || '');
    setClientId(s.client_id ? String(s.client_id) : '');
    setLugarId(s.lugar_id || null);
    setEspacioId(s.espacio_id || null);
    setDays(Array.isArray(s.days) && s.days.length
      ? s.days.map(d => ({ ...d, auto: false }))
      : [{ date: s.event_date || '', startTime: '', endTime: '' }]);
    setArmado({ date: s.armado_date || '', startTime: s.armado_start_time || '', endTime: s.armado_end_time || '' });
    setDesarmado({ date: s.teardown_date || '', startTime: s.teardown_start || '', endTime: s.teardown_end || '' });
    setBusinessUnit(s.business_unit || '');
    setEventType(s.event_type || '');
    setCurrency(s.currency || 'UYU');
    setPaymentTerms(s.payment_terms || '');
    setDiscountType(s.discount_type || 'PERCENT');
    setDiscountValue(String(Number(s.discount_value) || 0));
    setStatus(s.status || 'posible');
    setNotes(s.notes || '');
    setItems(initItems(s.items));
    setCommissions(initCommissions(s.commissions));
  };

  const handlePreviewVersion = async v => {
    if (!v.has_snapshot) {
      alert(`La v${v.version_number} es anterior al historial completo — no tiene datos para ver.`);
      return;
    }
    try {
      const { data } = await api.get(`/quotes/${id}/versions/${v.id}`);
      applySnapshot(data.snapshot);
      setPreviewVersion({ id: v.id, version_number: v.version_number });
    } catch (e) {
      alert(e.response?.data?.error || 'No se pudo cargar la versión');
    }
  };

  const handleBackToCurrent = async () => {
    if (!previewVersion) return;
    setPreviewVersion(null);
    await load(); // recarga el estado actual del servidor, descartando la vista
  };

  const handleRestoreVersion = () => {
    // Lo que está en pantalla (la versión vieja) pasa a ser el estado de
    // trabajo: se autoguarda y el botón Enviar late hasta el próximo envío.
    setPreviewVersion(null);
    dirty();
  };

  // ── Cambio de moneda ───────────────────────────────────────────────────────
  // Con ítems con precio pregunta: convertir con la cotización del día (tasa
  // editable) o cambiar solo la etiqueta de moneda sin tocar los números.
  function handleCurrencyChange(to) {
    if (to === currency) return;
    const hasPrices = items.some(it => !it.is_header && Number(it.unit_price) > 0);
    if (!hasPrices) { setCurrency(to); dirty(); return; }
    setCurrencyModal({ to, rate: '', fetching: true });
    api.get('/exchange/usd-uyu')
      .then(({ data }) => setCurrencyModal(m =>
        m && m.to === to ? { ...m, rate: data.rate ? String(data.rate) : '', fetching: false } : m))
      .catch(() => setCurrencyModal(m => m && m.to === to ? { ...m, fetching: false } : m));
  }

  function applyCurrency(convert) {
    const m = currencyModal;
    if (!m) return;
    if (convert) {
      const rate = Number(m.rate);
      if (!(rate > 0)) { alert('Poné una cotización válida (UYU por USD)'); return; }
      const factor = m.to === 'USD' ? 1 / rate : rate;
      const conv = v => Math.round((Number(v) || 0) * factor * 100) / 100;
      // '' = costo sin cargar; se preserva para no convertirlo en un 0 falso.
      setItems(prev => prev.map(it => it.is_header ? it : {
        ...it,
        unit_price: conv(it.unit_price),
        cost: it.cost === '' || it.cost == null ? it.cost : conv(it.cost),
      }));
      if (discountType === 'AMOUNT') setDiscountValue(v => String(conv(v)));
      // Comisión fija está en moneda; el % es independiente de la moneda.
      setCommissions(prev => prev.map(c => c.kind === 'fixed' && c.value !== '' && c.value != null
        ? { ...c, value: conv(c.value) } : c));
    }
    setCurrencyModal(null);
    setCurrency(m.to);
    dirty();
  }

  function buildPdfData() {
    const lugar = lugares.find(l => l.id === lugarId);
    const espacio = lugar?.espacios?.find(e => e.id === espacioId);
    const cli = clients.find(c => String(c.id) === String(clientId));
    return {
      folio: quote?.folio,
      title: title.trim(),
      clientName: cli ? cli.name : null,
      lugarLabel: lugar ? `${lugar.name}${espacio ? ` - ${espacio.name}` : ''}` : null,
      days: days.filter(d => d.date),
      currency,
      items,
      totals: computeTotals(items, discountType, discountValue, IVA_PCT),
      paymentTerms: paymentTerms || null,
      notes,
    };
  }

  // Previsualizar el PDF en una pestaña — sin enviar ni descargar.
  // La ventana se abre sincrónicamente (dentro del clic) para evitar el
  // bloqueador de popups; la URL del blob se asigna cuando está listo.
  const handleVerPdf = async () => {
    const w = window.open('', '_blank');
    try {
      const url = await generateQuotePdf(buildPdfData(), { mode: 'view' });
      if (w) w.location = url;
    } catch (e) {
      if (w) w.close();
      alert('No se pudo generar la vista previa');
    }
  };

  const handleEnviar = async (andPrint = false, equipmentMode = undefined) => {
    if (sendingRef.current) return; // ya hay un envío en vuelo (Enter repetido)
    if (previewVersion) { alert('Estás viendo una versión anterior — restaurala o volvé a la actual antes de enviar.'); return; }
    if (!title.trim()) { alert('El título es requerido'); return; }
    sendingRef.current = true; // bloquea autosaves nuevos desde ya (síncrono)
    setSending(true);
    try {
      // Un autosave ya despachado podría aterrizar DESPUÉS del Enviar y dejar
      // needs_send colgado en true — lo esperamos antes de enviar.
      if (autosaveInFlight.current) { try { await autosaveInFlight.current; } catch {} }
      const { data } = await api.post(`/quotes/${id}/enviar`, {
        ...buildPayload(),
        ...(equipmentMode ? { equipment_mode: equipmentMode } : {}),
      });
      if (data?.equipment_shortages?.length) setShortageNotice(data.equipment_shortages);
      setIsDirty(false);
      setLocalDirty(false);
      setEnviado(true);
      setHistoryKey(k => k + 1);
      await load();
      if (andPrint) {
        // PDF directo: se genera y descarga al instante, sin diálogo de impresión.
        await generateQuotePdf(buildPdfData());
      }
      setTimeout(() => setEnviado(false), 2500);
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.equipment_modified) {
        setEquipConflict({ ...e.response.data, andPrint });
      } else {
        alert(e.response?.data?.error || 'Error al enviar');
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  // ── IA: match automático de productos con equipo de depósito ──────────────
  // Al elegir un producto (o terminar de escribir texto libre), la IA sugiere
  // hasta 4 equipos del depósito. Con 1 clic la sugerencia queda reservada.

  const mergeMaterials = (existing, names) => {
    const seen = new Set((existing || []).map(m => m.item_name.toLowerCase()));
    const added = names
      .filter(n => !seen.has(n.toLowerCase()))
      .map(n => ({ item_name: n, quantity: 1 }));
    return added.length ? [...(existing || []), ...added] : existing;
  };

  const runAutoSuggest = useCallback(async (key, text, note) => {
    const t = (text || '').trim();
    if (t.length < 3) return;
    if (suggestingRef.current.has(key)) return; // ya hay una consulta en vuelo para este ítem
    suggestingRef.current.add(key);
    setSuggestingKeys(prev => prev.includes(key) ? prev : [...prev, key]);
    try {
      const { data } = await api.post('/ai/match-equipment', { text: t, description: note || undefined });
      const names = (data.materials || []).map(m => m.name).slice(0, 4);
      setItems(prev => prev.map(it => {
        if (it._key !== key) return it;
        const reserved = new Set((it.materials || []).map(m => m.item_name.toLowerCase()));
        // _suggestedFor recuerda qué texto ya se consultó — evita re-pagar la
        // misma consulta en cada blur aunque la IA no haya encontrado nada.
        return { ...it, _suggestions: names.filter(n => !reserved.has(n.toLowerCase())), _suggestedFor: t };
      }));
    } catch {
      // Silencioso: la sugerencia es asistiva, no bloquea el flujo.
    } finally {
      suggestingRef.current.delete(key);
      setSuggestingKeys(prev => prev.filter(k => k !== key));
    }
  }, []);

  const acceptSuggestion = useCallback((key, name) => {
    setItems(prev => prev.map(it => it._key === key ? {
      ...it,
      materials: mergeMaterials(it.materials, [name]),
      _suggestions: (it._suggestions || []).filter(n => n !== name),
    } : it));
    dirty();
  }, [dirty]);

  // Una propuesta se puede descartar entera sin tocar los equipos ya confirmados.
  const clearSuggestions = useCallback(key => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, _suggestions: [] } : it));
  }, []);

  // Propone equipos para los ítems aún sin reservas. La persona confirma cada
  // propuesta de forma explícita antes de que forme parte de la cotización.
  const suggestAllEquipment = async () => {
    const targets = items.filter(i => {
      const t = (i.description || '').trim();
      return !i.is_header && t.length >= 3 &&
        !(i.materials || []).length && (i._suggestedFor || '') !== t;
    });
    if (!targets.length) { alert('No hay ítems sin equipo para completar'); return; }
    setBulkSuggesting(true);
    try {
      await Promise.all(targets.map(t => runAutoSuggest(t._key, t.description, t.note)));
    } finally {
      setBulkSuggesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta cotización?')) return;
    setDeleting(true);
    try {
      await api.delete(`/quotes/${id}`);
      navigate('/ventas/cotizaciones');
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
      setDeleting(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
      <div className="spinner" />
    </div>
  );

  const statusOpt = STATUS_OPTS.find(s => s.value === status) || STATUS_OPTS[0];

  return (
    <div className="qp-wrap">
      <div className="qp-topbar">
        <div className="qp-heading">
          <button className="qp-back" onClick={() => navigate('/ventas/cotizaciones')} title="Volver a la lista">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Cotizaciones
          </button>
          <div className="qp-heading-row">
            <h1>{quote?.folio ? 'Cotización' : 'Nuevo presupuesto'}</h1>
            {quote?.folio && <span className="qp-folio">{quote.folio}</span>}
            <select
              className="qp-status-badge qp-status-select"
              style={{ background: `${statusOpt.color}22`, color: statusOpt.color }}
              value={status}
              disabled={!isAdmin}
              onChange={e => { setStatus(e.target.value); dirty(); }}
              title="Estado de la cotización"
            >
              {STATUS_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <p>Cotización comercial y planificación operativa</p>
        </div>
        <div className="qp-topbar-actions">
        {isAdmin && (
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleVerPdf}
              disabled={sending}
              style={{ fontSize: 13 }}
              title="Previsualizar el PDF en una pestaña, sin enviar"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
              </svg>
              Vista previa
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleSaveDraft}
              disabled={sending || savingDraft || !!previewVersion}
              style={{ fontSize: 13 }}
              title="Guardar cambios sin enviar la cotización"
            >
              {savingDraft ? 'Guardando…' : 'Guardar borrador'}
            </button>
            <button
              type="button"
              onClick={() => handleEnviar(false)}
              disabled={sending}
              className={`btn btn-primary${isDirty && !enviado ? ' btn-enviar-dirty' : ''}${enviado ? ' btn-enviado' : ''}`}
              style={{ fontSize: 14, padding: '7px 18px' }}
            >
              {sending ? 'Enviando…' : enviado ? '✓ Enviado' : (
                <>
                  Enviar a agenda
                  {isDirty && <span className="enviar-dot" />}
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-ghost qp-download-pdf"
              onClick={() => handleEnviar(true)}
              disabled={sending || !!previewVersion}
              title="Enviar y descargar el PDF"
            >
              PDF
            </button>
            <button className="qp-delete-action" onClick={handleDelete} disabled={deleting} title="Eliminar cotización">
              {deleting ? '…' : 'Eliminar'}
            </button>
          </>
        )}
        </div>
      </div>

      {previewVersion && (
        <div className="qp-preview-banner">
          <span>
            Viendo la <strong>v{previewVersion.version_number}</strong> — los cambios no se guardan hasta restaurar.
          </span>
          <div className="qp-preview-actions">
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12.5 }} onClick={handleBackToCurrent}>
              Volver a la actual
            </button>
            <button type="button" className="btn btn-primary" style={{ fontSize: 12.5 }} onClick={handleRestoreVersion}>
              Restaurar esta versión
            </button>
          </div>
        </div>
      )}

      {/* La HOJA (columna izquierda = exactamente lo que sale en el PDF) y el
          CARRIL derecho con lo interno, alineado banda por banda:
          armado ↔ horarios, equipo ↔ su ítem, versiones al final. */}
      <form className="qp-doc" onSubmit={e => { e.preventDefault(); if (!previewVersion) handleEnviar(false); }}>
        {/* Submit oculto: Enviar vive en el topbar, pero sin un submit interno
            el Enter en los campos dejaría de disparar el envío. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />

        {/* Banda: encabezado de la hoja — título + cliente/lugar */}
        <div className="qp-sheet qp-sheet-first qp-event-summary">
          <span className="qp-card-kicker">Datos del evento</span>
          <input
            className="qp-doc-title"
            value={title}
            onChange={e => { setTitle(e.target.value); dirty(); }}
            placeholder="Título del evento…"
            required
          />
          <div className="qp-doc-meta">
            <div>
              <span className="qp-meta-label">Cliente</span>
              <SearchSelect
                value={clientId || null}
                options={clients.map(c => {
                  const display = clientDisplayName(c);
                  return { key: c.id, label: c.name !== display ? `${display} — ${c.name}` : display };
                })}
                onSelect={o => { setClientId(o ? String(o.key) : ''); dirty(); }}
                placeholder="Buscar cliente…"
                clearLabel="Sin cliente"
                inputClassName="qp-seamless"
              />
            </div>
            <div>
              <span className="qp-meta-label">Lugar</span>
              <SearchSelect
                value={lugarId ? `${lugarId}:${espacioId || ''}` : null}
                options={lugares.flatMap(l => [
                  { key: `${l.id}:`, label: l.name, data: { lugarId: l.id, espacioId: null } },
                  ...(l.espacios || []).map(e => ({
                    key: `${l.id}:${e.id}`,
                    label: `${l.name} - ${e.name}`,
                    data: { lugarId: l.id, espacioId: e.id },
                  })),
                ])}
                onSelect={o => {
                  setLugarId(o?.data?.lugarId ?? null);
                  setEspacioId(o?.data?.espacioId ?? null);
                  dirty();
                }}
                placeholder="Buscar lugar…"
                clearLabel="Sin lugar"
                inputClassName="qp-seamless"
              />
            </div>
            <div>
              <span className="qp-meta-label">Unidad de negocio</span>
              <input
                className="form-input qp-seamless"
                value={businessUnit}
                onChange={e => { setBusinessUnit(e.target.value); dirty(); }}
                placeholder="Sin definir"
              />
            </div>
            <div>
              <span className="qp-meta-label">Tipo de evento</span>
              <input
                className="form-input qp-seamless"
                value={eventType}
                onChange={e => { setEventType(e.target.value); dirty(); }}
                placeholder="Sin definir"
              />
            </div>
          </div>
        </div>
        <div className="qp-margin qp-summary-history">
          <VersionHistory
            quoteId={id}
            refreshKey={historyKey}
            isDirty={isDirty && !previewVersion}
            previewVersion={previewVersion}
            onPreview={handlePreviewVersion}
            onBackToCurrent={handleBackToCurrent}
            onCreateVersion={handleCreateVersion}
            creatingVersion={creatingVersion}
            onDeleteVersion={handleDeleteVersion}
            deletingVersionId={deletingVersionId}
          />
        </div>

        {/* Bloque operativo: fechas, armado, desarmado y versiones. Estas
            fechas delimitan la ocupación real de los equipos. */}
        <div className="qp-sheet qp-event-schedule">
          <span className="qp-card-kicker">Fechas y horarios</span>
          <div className="qp-schedule-columns">
            <section className="qp-schedule-days">
              <span className="qp-schedule-section-label">Días del evento</span>
              <EventDays
                days={days}
                onDaysChange={v => { setDays(v); dirty(); }}
              />
            </section>
            <section className="qp-schedule-phases">
              <span className="qp-schedule-section-label">Fases operativas</span>
              <OperationPanel
                label="Armado"
                operation={armado}
                onChange={v => { setArmado(v); dirty(); }}
                note="✓ Restringe equipos desde esta fecha"
              />
              <OperationPanel
                label="Desarmado"
                operation={desarmado}
                onChange={v => { setDesarmado(v); dirty(); }}
                note="✓ Mantiene los equipos ocupados hasta este desarmado"
              />
            </section>
          </div>
        </div>
        <div className="qp-margin qp-schedule-versions">
          <OperationalSummary
            items={items}
            onSuggestAll={isAdmin ? suggestAllEquipment : undefined}
            suggesting={bulkSuggesting}
          />
        </div>

        {/* Bandas de ítems: hoja | equipo de depósito alineado por ítem */}
        <ItemsEditor
          items={items} products={products} suppliers={suppliers} currency={currency} inventoryNames={inventoryNames}
          availabilityByName={availabilityByName} demandByName={demandByName}
          onChange={v => { setItems(v); dirty(); }}
          onAutoSuggest={isAdmin ? runAutoSuggest : undefined}
          onAcceptSuggestion={acceptSuggestion}
          onClearSuggestions={clearSuggestions}
          suggestingKeys={suggestingKeys}
          onBulkSuggest={isAdmin ? suggestAllEquipment : undefined}
          bulkSuggesting={bulkSuggesting}
        />

        {/* Banda: totales + moneda */}
        <div className="qp-sheet qp-financial-sheet">
          <div className="qp-totals-band">
            <div>
              <span className="qp-meta-label">Moneda</span>
              <select className="form-input qp-moneda" value={currency} onChange={e => handleCurrencyChange(e.target.value)}>
                <option value="UYU">UYU</option>
                <option value="USD">USD</option>
              </select>
              <span className="qp-meta-label" style={{ display: 'block', marginTop: 10 }}>Forma de pago</span>
              <select className="form-input qp-moneda" value={paymentTerms} onChange={e => { setPaymentTerms(e.target.value); dirty(); }}>
                <option value="">—</option>
                <option value="Inmediato">Inmediato</option>
                <option value="Próxima semana">Próxima semana</option>
                <option value="30 días">30 días</option>
                <option value="A convenir">A convenir</option>
              </select>
            </div>
            <Totals
              items={items}
              discountType={discountType}
              discountValue={discountValue}
              currency={currency}
              onChange={(k, v) => {
                if (k === 'discountType') setDiscountType(v);
                if (k === 'discountValue') setDiscountValue(v);
                dirty();
              }}
            />
          </div>
        </div>
        <div className="qp-margin" />

        {/* Banda interna: comisiones + margen (equipo, no va al PDF). */}
        <div className="qp-sheet qp-internal-sheet">
          <InternalPanel
            items={items}
            commissions={commissions}
            suppliers={suppliers}
            discountType={discountType}
            discountValue={discountValue}
            currency={currency}
            onChange={v => { setCommissions(v); dirty(); }}
          />
        </div>
        <div className="qp-margin" />

        {/* Banda final: condiciones (cierra la hoja). */}
        <div className="qp-sheet qp-sheet-last qp-conditions-sheet">
          <span className="qp-meta-label">Condiciones generales</span>
          <textarea
            className="qp-notes"
            value={notes}
            onChange={e => { setNotes(e.target.value); dirty(); }}
            placeholder="Condiciones de pago, validez de la cotización, notas adicionales…"
            rows={3}
          />
        </div>
        <div className="qp-margin" />
      </form>

      {equipConflict && createPortal(
        // z-index sobre los menús de SearchSelect (9999); autoFocus saca el
        // foco del form para que Enter no re-dispare el submit por detrás.
        <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setEquipConflict(null)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Equipos modificados en planilla</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
              Desde el último envío se modificó la lista de equipos del proyecto en la planilla:
            </p>
            <ul style={{ color: 'var(--text-dim)', fontSize: 14, margin: '0 0 16px 18px' }}>
              {equipConflict.manual > 0 && <li>{equipConflict.manual} equipo{equipConflict.manual > 1 ? 's' : ''} agregado{equipConflict.manual > 1 ? 's' : ''} a mano</li>}
              {equipConflict.removed > 0 && <li>{equipConflict.removed} equipo{equipConflict.removed > 1 ? 's' : ''} de la cotización quitado{equipConflict.removed > 1 ? 's' : ''}</li>}
            </ul>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary"
                autoFocus
                onClick={() => { const c = equipConflict; setEquipConflict(null); handleEnviar(c.andPrint, 'diff'); }}
              >
                Actualizar — aplicar las diferencias
                <span style={{ display: 'block', fontSize: 12, fontWeight: 400, opacity: 0.8 }}>
                  Suma los cambios de la cotización y conserva lo que se tocó a mano en planilla.
                </span>
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => { const c = equipConflict; setEquipConflict(null); handleEnviar(c.andPrint, 'replace'); }}
              >
                Volver a agregar — como si fuese nuevo
                <span style={{ display: 'block', fontSize: 12, fontWeight: 400, opacity: 0.8 }}>
                  Deja la lista exactamente como la cotización. Se pierden los cambios manuales.
                </span>
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setEquipConflict(null)}>Cancelar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {shortageNotice && createPortal(
        <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setShortageNotice(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Se envió — con equipos sin unidad asignada</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 8 }}>
              Estos equipos quedaron en la lista del proyecto pero sin unidad física
              pre-asignada: están comprometidos en otros eventos en esas fechas o no
              hay stock suficiente en el depósito.
            </p>
            <ul style={{ color: 'var(--text)', fontSize: 14, margin: '0 0 16px 18px' }}>
              {shortageNotice.map(s => (
                <li key={s.item_name}>
                  <strong>{s.item_name}</strong> — faltan {s.missing} de {s.requested}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-primary" autoFocus onClick={() => setShortageNotice(null)}>
                Entendido
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {currencyModal && createPortal(
        <div className="modal-overlay" style={{ zIndex: 10000 }} onClick={() => setCurrencyModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Cambiar moneda a {currencyModal.to}</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 14, marginBottom: 12 }}>
              Hay ítems con precio. ¿Convertimos los valores o cambiamos solo la moneda?
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-dim)', marginBottom: 16 }}>
              Cotización (UYU por USD)
              <input
                className="form-input" type="number" min="0" step="any" autoFocus
                style={{ width: 100, textAlign: 'right' }}
                placeholder={currencyModal.fetching ? 'buscando…' : 'ej: 40.5'}
                value={currencyModal.rate}
                onChange={e => setCurrencyModal(m => ({ ...m, rate: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
              />
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button type="button" className="btn btn-primary" disabled={!(Number(currencyModal.rate) > 0)} onClick={() => applyCurrency(true)}>
                Convertir los valores
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => applyCurrency(false)}>
                Cambiar solo la moneda (no tocar números)
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setCurrencyModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
